import React from "react";
import { ModuleStep } from "../../constants/module";
import { ModuleValidation } from "../../store/features/moduleSlice";

interface ModuleNavigationProps {
  currentStep: number;
  steps: ModuleStep[];
  onStepChange: (step: number) => void;
  validation: ModuleValidation;
}

/**
 * Module Navigation Component
 * Provides step navigation for the module builder
 */
export const ModuleNavigation: React.FC<ModuleNavigationProps> = ({
  currentStep,
  steps,
  onStepChange,
  validation,
}) => {
  return (
    <div className="mb-6">
      <div className="flex flex-wrap gap-2">
        {steps.map((step, index) => {
          const isActive = index === currentStep;
          const isCompleted = index < currentStep;
          const hasErrors = validation.errors.length > 0 && index === currentStep;

          return (
            <button
              key={step.id}
              onClick={() => onStepChange(index)}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors
                ${isActive
                  ? "bg-highlightOrange text-black shadow-md"
                  : isCompleted
                  ? "bg-green-600 text-white hover:bg-green-700"
                  : "bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-400 dark:hover:bg-gray-600"
                }
                ${hasErrors ? "ring-2 ring-red-500 dark:ring-red-400" : ""}
              `}
            >
              <div className={`
                w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold
                ${isActive
                  ? "bg-black text-highlightOrange"
                  : isCompleted
                  ? "bg-white text-green-600"
                  : "bg-gray-500 dark:bg-gray-600 text-white"
                }
              `}>
                {isCompleted ? "✓" : index + 1}
              </div>
              <span className="hidden sm:inline">{step.label}</span>
              {hasErrors && (
                <span className="text-red-500 dark:text-red-400 text-sm">⚠️</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Step Progress Bar */}
      <div className="mt-4">
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div
            className="bg-highlightOrange h-2 rounded-full transition-all duration-300"
            style={{
              width: `${((currentStep + 1) / steps.length) * 100}%`,
            }}
          />
        </div>
        <div className="mt-2 text-sm text-gray-600 dark:text-gray-300 text-center">
          Step {currentStep + 1} of {steps.length}
        </div>
      </div>
    </div>
  );
};
